import React from 'react';
import { API_BASE, toUrl } from '../lib/config';

type SectionRow = {
  id: string;
  orderIndex: number;
  title: string;
  iconUrl?: string | null;
  isActive: boolean;
  updatedAt: string;
};

type LineRow = {
  id: string;
  sectionId: string;
  orderIndex: number;
  text: string;
  href?: string | null;
  isActive: boolean;
  updatedAt: string;
};

const ContactYonetim: React.FC = () => {

  const [sections, setSections] = React.useState<SectionRow[]>([]);
  const [loadingSec, setLoadingSec] = React.useState(false);
  const [error, setError] = React.useState('');
  const [q, setQ] = React.useState('');

  const [selectedSection, setSelectedSection] = React.useState<SectionRow | null>(null);
  const [lines, setLines] = React.useState<LineRow[]>([]);
  const [loadingLines, setLoadingLines] = React.useState(false);

  // Inline edit states for lines
  const [editingLineId, setEditingLineId] = React.useState('');
  const [eText, setEText] = React.useState('');
  const [eHref, setEHref] = React.useState('');
  const [eOrder, setEOrder] = React.useState<number>(0);
  const [eActive, setEActive] = React.useState(true);
  const [savingLine, setSavingLine] = React.useState(false);

  const fetchSections = React.useCallback(async () => {
    try {
      setLoadingSec(true); setError('');
      const res = await fetch(toUrl('/api/contact/admin/sections'));
      if (!res.ok) throw new Error('Bölüm listesi alınamadı');
      const data = await res.json();
      setSections(data || []);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoadingSec(false); }
  }, []);

  const fetchLines = React.useCallback(async (sectionId: string) => {
    try {
      setLoadingLines(true); setError('');
      const res = await fetch(toUrl(`/api/contact/admin/sections/${sectionId}/lines`));
      if (!res.ok) throw new Error('Satırlar alınamadı');
      const data = await res.json();
      setLines(data || []);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoadingLines(false); }
  }, []);

  React.useEffect(() => { fetchSections(); }, [fetchSections]);

  const filtered = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return sections;
    return sections.filter(s => (s.title || '').toLowerCase().includes(term));
  }, [sections, q]);

  const openSection = (row: SectionRow) => { setSelectedSection(row); fetchLines(row.id); };
  const closeSection = () => { setSelectedSection(null); setLines([]); setEditingLineId(''); };

  const openEditLine = (ln: LineRow) => {
    setEditingLineId(ln.id);
    setEText(ln.text || '');
    setEHref(ln.href || '');
    setEOrder(ln.orderIndex || 0);
    setEActive(!!ln.isActive);
  };
  const cancelEditLine = () => { setEditingLineId(''); setEText(''); setEHref(''); setEOrder(0); setEActive(true); };

  const onSaveLine = async (id: string) => {
    if (!eText.trim()) { setError('Lütfen metin girin'); return; }
    try {
      setSavingLine(true); setError('');
      const body: any = { text: eText.trim(), href: eHref.trim() || null, orderIndex: eOrder || 0, isActive: !!eActive };
      const res = await fetch(toUrl(`/api/contact/admin/lines/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      cancelEditLine(); if (selectedSection) await fetchLines(selectedSection.id);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSavingLine(false); }
  };

  return (
    <div className="admin-page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>İletişim Bölümleri</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Toplam: {sections.length}</div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara: başlık" className="input" style={{ width: 280 }} />
            {q && <button onClick={() => setQ('')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Temizle</button>}
          </div>
          <button onClick={fetchSections} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Sıra</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Başlık</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loadingSec && (<tr><td colSpan={5} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
              {!loadingSec && filtered.length === 0 && (<tr><td colSpan={5} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
              {filtered.map(row => (
                <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px' }}>{row.orderIndex}</td>
                  <td style={{ padding: '12px 16px' }}>{row.title}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: row.isActive ? '#ecfdf5' : '#fef2f2', color: row.isActive ? '#065f46' : '#991b1b', border: `1px solid ${row.isActive ? '#a7f3d0' : '#fecaca'}` }}>{row.isActive ? 'Aktif' : 'Pasif'}</span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button onClick={() => openSection(row)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #0ea5e9', background: '#fff', color: '#0ea5e9', cursor: 'pointer' }}>İçerikler</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lines for selected section */}
      {selectedSection && (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h4 style={{ margin: 0 }}>Seçili Bölüm: {selectedSection.title}</h4>
            <button type="button" onClick={closeSection} aria-label="Kapat" style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
          </div>

          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 600 }}>Satırlar</div>
              <button onClick={() => fetchLines(selectedSection.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Metin</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Bağlantı (href)</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Sıra</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                    <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingLines && (<tr><td colSpan={6} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
                  {!loadingLines && lines.length === 0 && (<tr><td colSpan={6} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
                  {lines.map(ln => (
                    <tr key={ln.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px 16px', minWidth: 280 }}>
                        {editingLineId === ln.id ? (
                          <textarea className="input" rows={3} value={eText} onChange={(e) => setEText(e.target.value)} />
                        ) : ln.text}
                      </td>
                      <td style={{ padding: '12px 16px', minWidth: 240 }}>
                        {editingLineId === ln.id ? (
                          <input className="input" type="text" value={eHref} onChange={(e) => setEHref(e.target.value)} placeholder="https://..." />
                        ) : (ln.href || '-')}
                      </td>
                      <td style={{ padding: '12px 16px', width: 120 }}>
                        {editingLineId === ln.id ? (
                          <input className="input" type="number" value={eOrder} onChange={(e) => setEOrder(parseInt(e.target.value || '0', 10))} />
                        ) : ln.orderIndex}
                      </td>
                      <td style={{ padding: '12px 16px', width: 160 }}>
                        {editingLineId === ln.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input id={`act-${ln.id}`} type="checkbox" checked={eActive} onChange={(e) => setEActive(e.target.checked)} />
                            <label htmlFor={`act-${ln.id}`}>Aktif</label>
                          </div>
                        ) : (
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: ln.isActive ? '#ecfdf5' : '#fef2f2', color: ln.isActive ? '#065f46' : '#991b1b', border: `1px solid ${ln.isActive ? '#a7f3d0' : '#fecaca'}` }}>{ln.isActive ? 'Aktif' : 'Pasif'}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>{ln.updatedAt ? new Date(ln.updatedAt).toLocaleString() : '-'}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', minWidth: 260 }}>
                        {editingLineId === ln.id ? (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button onClick={() => onSaveLine(ln.id)} disabled={savingLine} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: savingLine ? 0.8 : 1 }}>{savingLine ? 'Kaydediliyor...' : 'Kaydet'}</button>
                            <button onClick={cancelEditLine} disabled={savingLine} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => openEditLine(ln)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
        </div>
      )}

      {error && !selectedSection && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
    </div>
  );
};

export default ContactYonetim;
